// Follow-up suggestion generation for TheoryCoach - extracted to reduce theoryCoach.ai size

import { logger } from '../utils/logger';
import { getLanguageName } from './theoryCoach.core';
import { getTemplateSuggestions } from './theoryCoach.templates';
import { callOpenAI } from './theoryCoach.openai';

/**
 * Generate AI-powered contextual follow-up suggestion cards
 */
export const generateFollowUpSuggestions = async (
  openai: any,
  useOpenAI: boolean,
  params: {
    language: string;
    questionText?: string;
    subject?: string;
    hasImage?: boolean;
    correctAnswer?: string;
    explanation?: string;
    userLastMessage?: string;
    coachLastResponse?: string;
    conversationHistory?: Array<{ role: 'user' | 'coach'; content: string }>;
    isCorrect?: boolean;
    roundNumber?: number;
    lockedMisconceptionId?: string | null;
    questionId?: string;
    wrongOptions?: Array<{ id: string; text: string }>;
  }
): Promise<string[]> => {
  const { language, questionText, subject, hasImage, correctAnswer, explanation, userLastMessage, coachLastResponse, conversationHistory, isCorrect, roundNumber, lockedMisconceptionId, questionId, wrongOptions } = params;

  const currentRound = roundNumber || 1;
  const genericCard = language === 'nl' ? 'Ik gokte' : language === 'ar' ? 'خمّنت' : 'I guessed';

  if (!useOpenAI || !openai) {
    return getTemplateSuggestions(language, questionText, subject, hasImage, genericCard, isCorrect, roundNumber);
  }

  try {
    const languageName = getLanguageName(language);

    let conversationContext = '';
    if (coachLastResponse) {
      conversationContext = `\n\nLAST COACH MESSAGE (CARDS MUST MATCH THIS):\nCOACH: ${coachLastResponse}`;
      if (conversationHistory && conversationHistory.length > 0) {
        const recentHistory = conversationHistory.slice(-6);
        conversationContext += '\n\nFULL CONVERSATION HISTORY:\n' + recentHistory
          .map(msg => `${msg.role === 'user' ? 'USER' : 'COACH'}: ${msg.content}`)
          .join('\n');
      } else if (userLastMessage) {
        conversationContext += `\n\nUSER LAST MESSAGE:\nUSER: ${userLastMessage}`;
      }
    } else if (conversationHistory && conversationHistory.length > 0) {
      conversationContext = '\n\nCONVERSATION HISTORY:\n' + conversationHistory
        .slice(-4)
        .map(msg => `${msg.role === 'user' ? 'USER' : 'COACH'}: ${msg.content}`)
        .join('\n');
    } else if (userLastMessage) {
      conversationContext = `\n\nCONVERSATION:\nUSER: ${userLastMessage}`;
    }

    const isCorrectAnswer = isCorrect !== undefined
      ? isCorrect
      : conversationHistory?.some(msg =>
          msg.role === 'coach' &&
          (typeof msg.content === 'string' ? msg.content : String(msg.content)).toLowerCase().includes('correct')
        ) || false;

    if (questionId && wrongOptions && wrongOptions.length >= 2) {
      const { generateMisconceptionMap, getMisconceptionCards } = await import('./misconceptionMapping');
      const map = generateMisconceptionMap(
        questionId,
        questionText || '',
        subject || '',
        correctAnswer || '',
        explanation || '',
        wrongOptions,
        language as 'en' | 'nl' | 'ar'
      );

      if (lockedMisconceptionId) {
        const cards = getMisconceptionCards(map, lockedMisconceptionId, currentRound as 1 | 2, language as 'en' | 'nl' | 'ar');
        if (cards.length >= 2) {
          const uniqueCards = cards
            .map(card => card.text)
            .filter((text, index, self) => self.indexOf(text) === index);
          if (uniqueCards.length >= 2) {
            return uniqueCards.slice(0, 2);
          } else if (uniqueCards.length === 1) {
            return [uniqueCards[0], uniqueCards[0]];
          }
        }
      } else if (currentRound === 1 && !isCorrectAnswer) {
        const misconceptionIds = Object.keys(map.misconceptions);
        if (misconceptionIds.length >= 2) {
          const firstMisconceptionCards = getMisconceptionCards(map, misconceptionIds[0], 1, language as 'en' | 'nl' | 'ar');
          const secondMisconceptionCards = getMisconceptionCards(map, misconceptionIds[1], 1, language as 'en' | 'nl' | 'ar');
          if (firstMisconceptionCards.length > 0 && secondMisconceptionCards.length > 0) {
            const card1 = firstMisconceptionCards[0].text;
            const card2 = secondMisconceptionCards[0].text;
            if (card1 !== card2) {
              return [card1, card2];
            } else {
              if (secondMisconceptionCards.length > 1) {
                return [card1, secondMisconceptionCards[1].text];
              } else if (firstMisconceptionCards.length > 1) {
                return [firstMisconceptionCards[1].text, card2];
              }
            }
          }
        } else if (misconceptionIds.length === 1) {
          const cards = getMisconceptionCards(map, misconceptionIds[0], 1, language as 'en' | 'nl' | 'ar');
          if (cards.length >= 2) {
            const uniqueCards = cards
              .map(card => card.text)
              .filter((text, index, self) => self.indexOf(text) === index);
            if (uniqueCards.length >= 2) {
              return uniqueCards.slice(0, 2);
            } else if (uniqueCards.length === 1) {
              logger.warn(`Only one unique card found for misconception ${misconceptionIds[0]}, expected 2`);
              return [uniqueCards[0], uniqueCards[0]];
            }
          }
        }
      }
    }

    const misconceptionLockWarning = lockedMisconceptionId
      ? `\n\n⚠️ CRITICAL: MISCONCEPTION LOCKED (${lockedMisconceptionId})\n- You MUST generate cards that address ONLY this locked misconception\n- You are NOT allowed to introduce new misconceptions\n- You are NOT allowed to jump to unrelated reasoning\n- All cards MUST stay within the same logical reasoning chain\n- If you cannot generate valid cards for this misconception, you MUST fail and use fallback\n`
      : '';

    const prompt = `You are TheoryCoach generating 2 ADAPTIVE follow-up suggestion cards using STRICT MISCONCEPTION-BASED LOGIC.

${misconceptionLockWarning}

⚠️ QUESTION-SCOPED FOLLOW-UPS — STRICT RULE ⚠️
ALL follow-up cards MUST be scoped to THIS SPECIFIC QUESTION (Question ID: ${questionId || 'N/A'}).

QUESTION CONTEXT (MANDATORY - ALL CARDS MUST REFERENCE THIS):
QUESTION ID: ${questionId || 'N/A'}
QUESTION TEXT: ${questionText || 'N/A'}
TOPIC: ${subject || 'General'}
HAS IMAGE: ${hasImage ? 'Yes (road sign question)' : 'No'}
CORRECT ANSWER: ${correctAnswer || 'N/A'}
EXPLANATION: ${explanation ? explanation.substring(0, 300) : 'N/A'}
USER ANSWERED: ${isCorrectAnswer ? 'CORRECT' : 'INCORRECT'}${conversationContext}

STRICT QUESTION-SCOPING RULES:
1) Every follow-up card MUST be bound to THIS question (${questionId || 'current question'})
   - Cards may NOT be reused across different questions
   - Even if the misconception and rule are the same, wording must adapt to THIS question's scenario

2) Follow-up cards MUST reference at least one of:
   - The specific scenario in THIS question: "${questionText || 'N/A'}"
   - The selected wrong answer option (if applicable)
   - A visible element in the image (if present): ${hasImage ? 'YES - reference specific sign/element' : 'No image'}
   - A detail unique to THIS question (not generic)

3) You are NOT allowed to:
   - Show generic follow-up cards that could apply to any question
   - Reuse phrasing from previous questions
   - Ask reasoning questions that do not clearly relate to THIS question's scenario
   - Generate cards that sound interchangeable with another question

4) If a suitable question-specific follow-up cannot be produced:
   - DO NOT generate generic fallback cards
   - Return empty array (let system handle fallback)
   - Do NOT fall back to generic cards

STRICT FOLLOW-UP LOGIC — MANDATORY ENFORCEMENT:

Maximum: 2 follow-up turns (Phase 1 → Phase 2 → final)

PHASE 1 — REFLECTION (After wrong answer):
- Ask ONE variation prompt: "What made this option feel right?" / "What convinced you here?" / "Why did this answer seem correct in this situation?"
- Present exactly TWO follow-up cards
- Both cards must be question-specific
- Both must represent different possible reasons
- Each card MUST map to a DIFFERENT misconception ID for that question
- Cards must describe *reasoning*, not facts
- Examples: "I misread the sign", "I guessed", "I don't understand this rule", "What did I miss?"
- Cards disappear immediately after selection

When user selects a card:
- Lock the misconception
- Advance to Phase 2
- Respond ONLY to the selected card's intent (1–2 short sentences)
- No general rules yet

MISCONCEPTION LOCK (CRITICAL):
${lockedMisconceptionId
  ? `- MISCONCEPTION IS LOCKED: ${lockedMisconceptionId}\n- From this point forward, ALL follow-ups, responses, and cards MUST:\n  - Address ONLY this misconception\n  - Stay within the same logical reasoning chain\n- You are NOT allowed to:\n  - Introduce new misconceptions\n  - Jump to unrelated reasoning\n  - Ask generic reflection questions`
  : `- No misconception locked yet (Phase 1)\n- When user selects a card, that misconception will be locked\n- After locking, all subsequent cards MUST address ONLY that misconception`}

PHASE 2 — CONVERGENCE:
- Present exactly TWO NEW follow-up cards
- These cards MUST:
  - Relate to the SAME locked misconception
  - Be MORE specific or corrective
  - NOT repeat wording or intent
  - Narrow the reasoning toward the correct rule
  - Be mutually exclusive but logically aligned
- If a card does not logically follow from the first choice, it MUST NOT be shown

When user selects a card:
- Cards disappear
- Respond briefly (1–2 sentences)
- Immediately deliver the TAKE-HOME RULE

CURRENT PHASE: ${roundNumber || 1}
- Phase 1: Reflection (why did you choose this?) - Map to different misconceptions
- Phase 2: Convergence (brief response only) - take-home rule is added separately by the system

CRITICAL: Cards MUST use MAIN QUESTION CONTEXT for logical follow-ups:
- PRIMARY: Cards must address the MAIN QUESTION topic (${subject || 'General'}) and the user's confusion about it
- Cards should help the user understand the QUESTION's core concept, not just react to the last phrase
- If the question is about traffic lights: Cards should be about traffic light rules, not generic "read" or "time" advice
- If the question is about road signs: Cards should be about sign recognition, not unrelated topics
- If the user said "I misread the question": Cards should address what they misread in THIS SPECIFIC QUESTION (e.g., "What part of the traffic light rule?", "Which sign detail did I miss?")
- Use the EXPLANATION to understand what the user needs to learn about THIS QUESTION
- The coach's last message provides guidance, but cards must connect back to the MAIN QUESTION context
- NEVER generate cards that only extract keywords from the coach's last message without connecting to the question
- Cards should make LOGICAL SENSE in the context of the MAIN QUESTION and user's actual confusion
- Examples of LOGICAL cards:
  - Question about amber lights, user misread: "What does amber mean in this context?", "When must I stop at amber?"
  - Question about priority, user confused: "I don't understand priority rules", "Which vehicle has priority here?"
  - Question about signs, user guessed: "What should I look for on this sign?", "Which sign detail matters?"
- Examples of ILLOGICAL cards (avoid):
  - Coach says "read carefully" → Cards: "Tell me more about read" (too literal, doesn't connect to question)
  - Coach says "take your time" → Cards: "What does time mean?" (not related to question context)

GENERATE EXACTLY 2 CARDS based on USER ANSWERED status and PHASE:

STRICT: Maximum 2 follow-up turns (Phase 1 → Phase 2 → final take-home rule)

${isCorrectAnswer ? `
IF USER ANSWERED CORRECT:
- No follow-ups for correct answers
- Show explanation and Continue CTA immediately
` : `
IF USER ANSWERED INCORRECT:
${currentRound === 1 ? `
PHASE 1 — REFLECTION:
- Ask variation prompt: "What made this option feel right?" / "What convinced you here?" / "Why did this answer seem correct in this situation?"
- Show EXACTLY 2 cards
- Both cards must be question-specific
- Both must represent different possible reasons
- Each card MUST map to a DIFFERENT misconception ID
- Examples: "I misread the sign", "I guessed", "I don't understand this rule", "What did I miss?"
- NEVER use "Explain further" - use context-specific questions
- Cards disappear immediately after selection
` : `
PHASE 2 — CONVERGENCE:
- Show EXACTLY 2 NEW cards
- Must relate to the SAME locked misconception
- Must be MORE specific or corrective
- Must NOT repeat wording or intent
- Examples: "What did I misunderstand about this rule?", "Which part of this scenario confused me?", "I need clarity on this specific point"
- After user selects card: respond BRIEFLY (1 sentence only) - take-home rule is added separately
`}
`}

REQUIREMENTS:
- Cards must be short (5-8 words max each)
- Cards MUST be question-specific - reference THIS question's scenario, topic, or elements
- Cards MUST match the LAST coach message - reference specific terms/concepts from it
- Cards MUST be bound to Question ID: ${questionId || 'current question'} - cannot be reused across questions
- NEVER repeat previous cards - always generate NEW ones
- NEVER use generic cards that could apply to any question
- NEVER use "Explain further" - always use context-specific, adaptive cards that fit the logical flow
- Cards should feel like a natural continuation of the conversation about THIS SPECIFIC QUESTION
- If you cannot generate question-specific cards, return empty (do NOT use generic fallbacks)
- Respond in ${languageName}
- Return ONLY the 2 cards, one per line, no numbering, no explanation, no quotes

Example: Question is about traffic lights, coach says "Amber means stop unless you've already crossed the line."
Good cards (connect to MAIN QUESTION about traffic lights):
I don't understand amber lights
When must I stop at amber?

Another good example: Question is about priority rules, coach said "The rule here is priority to the right."
Good cards (connect to MAIN QUESTION about priority):
I don't understand priority rules
Which vehicle has priority here?

Another example: Question is about traffic lights, user said "I misread the question", coach said "Take your time, read carefully."
Good cards (connect to MAIN QUESTION, not just coach's advice):
What part of the traffic light rule did I miss?
Which light detail should I focus on?

Bad cards (too literal, don't connect to question):
Tell me more about read (extracts keyword without question context)
What does time mean? (not related to traffic light question)
I guessed (too generic, doesn't address question-specific confusion)

Your 2 adaptive cards that match the last coach message (one per line):`;

    const response = await Promise.race([
      callOpenAI(openai, prompt, language),
      new Promise<string>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 3000)
      )
    ]);

    const lines = response.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .slice(0, 2);

    if (lines.length === 2) {
      return lines;
    }

    const usedCards = conversationHistory
      ?.filter(msg => msg.role === 'user')
      .map(msg => {
        const content = typeof msg.content === 'string' ? msg.content : String(msg.content);
        return content.toLowerCase().trim();
      }) || [];

    const questionSubject = (subject || '').toLowerCase();
    const questionTextLower = (questionText || '').toLowerCase();
    const explanationLower = (explanation || '').toLowerCase();

    const questionKeyTerms: string[] = [];
    if (questionSubject.includes('traffic light') || questionTextLower.includes('traffic light') || explanationLower.includes('traffic light') ||
        questionSubject.includes('verkeerslicht') || questionTextLower.includes('verkeerslicht')) {
      questionKeyTerms.push('traffic light');
    }
    if (questionSubject.includes('amber') || questionTextLower.includes('amber') || explanationLower.includes('amber') ||
        questionSubject.includes('oranje') || questionTextLower.includes('oranje')) {
      questionKeyTerms.push('amber');
    }
    if (questionSubject.includes('priority') || questionTextLower.includes('priority') || explanationLower.includes('priority') ||
        questionTextLower.includes('right of way') || questionTextLower.includes('voorrang')) {
      questionKeyTerms.push('priority');
    }
    if (questionSubject.includes('sign') || questionTextLower.includes('sign') || hasImage ||
        questionSubject.includes('bord') || questionTextLower.includes('bord')) {
      questionKeyTerms.push('sign');
    }
    if (questionSubject.includes('speed') || questionTextLower.includes('speed') || explanationLower.includes('speed') ||
        questionSubject.includes('snelheid')) {
      questionKeyTerms.push('speed');
    }
    if (questionSubject.includes('stop') || questionTextLower.includes('stop') || explanationLower.includes('stop')) {
      questionKeyTerms.push('stop');
    }

    if (questionKeyTerms.length > 0) {
      const mainTerm = questionKeyTerms[0];
      let contextCards: string[];

      if (currentRound === 2) {
        if (language === 'nl') {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['Wat betekent amber in deze context?', 'Wanneer moet ik stoppen bij amber?'];
          } else if (mainTerm === 'priority') {
            contextCards = ['Ik begrijp voorrangsregels niet', 'Welk voertuig heeft voorrang hier?'];
          } else if (mainTerm === 'sign') {
            contextCards = ['Welk bord detail moet ik zien?', 'Wat moet ik zoeken op dit bord?'];
          } else {
            contextCards = ['Wat begreep ik verkeerd?', 'Welk deel verwarde me?'];
          }
        } else if (language === 'ar') {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['ماذا يعني البرتقالي في هذا السياق؟', 'متى يجب أن أتوقف عند البرتقالي؟'];
          } else if (mainTerm === 'priority') {
            contextCards = ['لا أفهم قواعد الأولوية', 'أي مركبة لها الأولوية هنا؟'];
          } else if (mainTerm === 'sign') {
            contextCards = ['أي تفصيل في اللوحة يجب أن أرى؟', 'ماذا يجب أن أبحث عنه على هذه اللوحة؟'];
          } else {
            contextCards = ['ماذا فهمت بشكل خاطئ؟', 'أي جزء أربكني؟'];
          }
        } else {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['What does amber mean in this context?', 'When must I stop at amber?'];
          } else if (mainTerm === 'priority') {
            contextCards = ['I don\'t understand priority rules', 'Which vehicle has priority here?'];
          } else if (mainTerm === 'sign') {
            contextCards = ['Which sign detail should I look for?', 'What should I focus on this sign?'];
          } else {
            contextCards = ['What did I misunderstand?', 'Which part confused me?'];
          }
        }
      } else if (currentRound === 3) {
        if (language === 'nl') {
          contextCards = ['Wat moet ik onthouden?', 'Klaar om door te gaan'];
        } else if (language === 'ar') {
          contextCards = ['ماذا يجب أن أتذكر؟', 'جاهز للمتابعة'];
        } else {
          contextCards = ['What should I remember?', 'Ready to move on'];
        }
      } else {
        if (language === 'nl') {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['Ik begrijp amber lichten niet', 'Wanneer moet ik stoppen bij amber?'];
          } else if (mainTerm === 'priority') {
            contextCards = ['Ik begrijp voorrangsregels niet', 'Welk voertuig heeft voorrang?'];
          } else if (mainTerm === 'sign') {
            contextCards = ['Welk bord detail miste ik?', 'Wat moet ik zoeken op dit bord?'];
          } else {
            contextCards = ['Wat deel van de vraag mis ik?', 'Welk detail is belangrijk?'];
          }
        } else if (language === 'ar') {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['لا أفهم أضواء البرتقالي', 'متى يجب أن أتوقف عند البرتقالي؟'];
          } else if (mainTerm === 'priority') {
            contextCards = ['لا أفهم قواعد الأولوية', 'أي مركبة لها الأولوية؟'];
          } else if (mainTerm === 'sign') {
            contextCards = ['أي تفصيل في اللوحة فاتني؟', 'ماذا يجب أن أبحث عنه على هذه اللوحة؟'];
          } else {
            contextCards = ['أي جزء من السؤال فاتني؟', 'أي تفصيل مهم؟'];
          }
        } else {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['I don\'t understand amber lights', 'When must I stop at amber?'];
          } else if (mainTerm === 'priority') {
            contextCards = ['I don\'t understand priority rules', 'Which vehicle has priority?'];
          } else if (mainTerm === 'sign') {
            contextCards = ['Which sign detail did I miss?', 'What should I look for on this sign?'];
          } else {
            contextCards = ['What part of the question did I miss?', 'Which detail is important?'];
          }
        }
      }

      const filteredCards = contextCards.filter((card: string) => {
        const cardLower = card.toLowerCase().trim();
        return !usedCards.some(used =>
          used.includes(cardLower) || cardLower.includes(used) || used === cardLower
        );
      });

      if (filteredCards.length >= 2) {
        return filteredCards.slice(0, 2);
      }
    }

    const templateCards = getTemplateSuggestions(language, questionText, subject, hasImage, genericCard, isCorrect, currentRound);
    const questionTextLowerForFilter = (questionText || '').toLowerCase();
    const subjectLower = (subject || '').toLowerCase();
    const questionSpecificTemplateCards = templateCards.filter((card: string) => {
      const cardLower = card.toLowerCase();
      const isQuestionSpecific = cardLower.includes(subjectLower) ||
             cardLower.includes(questionTextLowerForFilter.substring(0, 20)) ||
             (cardLower.includes('this') || cardLower.includes('here') || cardLower.includes('deze') || cardLower.includes('هذا'));
      const isGeneric = !isQuestionSpecific &&
                       (cardLower.includes('explain') || cardLower === genericCard.toLowerCase());
      return isQuestionSpecific && !isGeneric;
    });

    const filteredTemplateCards = questionSpecificTemplateCards.filter((card: string) => {
      const cardLower = card.toLowerCase().trim();
      return !usedCards.some(used =>
        used.includes(cardLower) || cardLower.includes(used) || used === cardLower
      );
    });

    if (filteredTemplateCards.length >= 2) {
      return filteredTemplateCards.slice(0, 2);
    } else if (currentRound === 2) {
      if (language === 'nl') {
        return ['Wat begreep ik verkeerd?', 'Welk deel verwarde me?'];
      } else if (language === 'ar') {
        return ['ماذا فهمت بشكل خاطئ؟', 'أي جزء أربكني؟'];
      }
      return ['What did I misunderstand?', 'Which part confused me?'];
    } else if (currentRound === 3) {
      return [];
    } else {
      if (language === 'nl') {
        return ['Wat bedoel je precies?', 'Kun je dat uitleggen?'];
      } else if (language === 'ar') {
        return ['ماذا تقصد بالضبط؟', 'هل يمكنك شرح ذلك؟'];
      }
      return ['What do you mean exactly?', 'Can you explain that?'];
    }
  } catch (error) {
    logger.debug('AI suggestion generation failed, using question-context fallback:', error);
    const usedCards = conversationHistory
      ?.filter(msg => msg.role === 'user')
      .map(msg => {
        const content = typeof msg.content === 'string' ? msg.content : String(msg.content);
        return content.toLowerCase().trim();
      }) || [];

    const questionSubject = (subject || '').toLowerCase();
    const questionTextLower = (questionText || '').toLowerCase();
    const explanationLower = (explanation || '').toLowerCase();

    const questionKeyTerms: string[] = [];
    if (questionSubject.includes('traffic light') || questionTextLower.includes('traffic light') || explanationLower.includes('traffic light') ||
        questionSubject.includes('verkeerslicht') || questionTextLower.includes('verkeerslicht')) {
      questionKeyTerms.push('traffic light');
    }
    if (questionSubject.includes('amber') || questionTextLower.includes('amber') || explanationLower.includes('amber') ||
        questionSubject.includes('oranje') || questionTextLower.includes('oranje')) {
      questionKeyTerms.push('amber');
    }
    if (questionSubject.includes('priority') || questionTextLower.includes('priority') || explanationLower.includes('priority') ||
        questionTextLower.includes('right of way') || questionTextLower.includes('voorrang')) {
      questionKeyTerms.push('priority');
    }
    if (questionSubject.includes('sign') || questionTextLower.includes('sign') || hasImage ||
        questionSubject.includes('bord') || questionTextLower.includes('bord')) {
      questionKeyTerms.push('sign');
    }
    if (questionSubject.includes('speed') || questionTextLower.includes('speed') || explanationLower.includes('speed') ||
        questionSubject.includes('snelheid')) {
      questionKeyTerms.push('speed');
    }

    if (questionKeyTerms.length > 0) {
      const mainTerm = questionKeyTerms[0];
      let contextCards: string[];

      if (currentRound === 2) {
        if (language === 'nl') {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['Wat betekent amber in deze context?', 'Wanneer moet ik stoppen bij amber?'];
          } else if (mainTerm === 'priority') {
            contextCards = ['Ik begrijp voorrangsregels niet', 'Welk voertuig heeft voorrang hier?'];
          } else if (mainTerm === 'sign') {
            contextCards = ['Welk bord detail moet ik zien?', 'Wat moet ik zoeken op dit bord?'];
          } else {
            contextCards = ['Wat begreep ik verkeerd?', 'Welk deel verwarde me?'];
          }
        } else if (language === 'ar') {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['ماذا يعني البرتقالي في هذا السياق؟', 'متى يجب أن أتوقف عند البرتقالي؟'];
          } else if (mainTerm === 'priority') {
            contextCards = ['لا أفهم قواعد الأولوية', 'أي مركبة لها الأولوية هنا؟'];
          } else if (mainTerm === 'sign') {
            contextCards = ['أي تفصيل في اللوحة يجب أن أرى؟', 'ماذا يجب أن أبحث عنه على هذه اللوحة؟'];
          } else {
            contextCards = ['ماذا فهمت بشكل خاطئ؟', 'أي جزء أربكني؟'];
          }
        } else {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['What does amber mean in this context?', 'When must I stop at amber?'];
          } else if (mainTerm === 'priority') {
            contextCards = ['I don\'t understand priority rules', 'Which vehicle has priority here?'];
          } else if (mainTerm === 'sign') {
            contextCards = ['Which sign detail should I look for?', 'What should I focus on this sign?'];
          } else {
            contextCards = ['What did I misunderstand?', 'Which part confused me?'];
          }
        }
      } else if (currentRound === 3) {
        if (language === 'nl') {
          contextCards = ['Wat moet ik onthouden?', 'Klaar om door te gaan'];
        } else if (language === 'ar') {
          contextCards = ['ماذا يجب أن أتذكر؟', 'جاهز للمتابعة'];
        } else {
          contextCards = ['What should I remember?', 'Ready to move on'];
        }
      } else {
        if (language === 'nl') {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['Ik begrijp amber lichten niet', 'Wanneer moet ik stoppen bij amber?'];
          } else if (mainTerm === 'priority') {
            contextCards = ['Ik begrijp voorrangsregels niet', 'Welk voertuig heeft voorrang?'];
          } else if (mainTerm === 'sign') {
            contextCards = ['Welk bord detail miste ik?', 'Wat moet ik zoeken op dit bord?'];
          } else {
            contextCards = ['Wat deel van de vraag mis ik?', 'Welk detail is belangrijk?'];
          }
        } else if (language === 'ar') {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['لا أفهم أضواء البرتقالي', 'متى يجب أن أتوقف عند البرتقالي؟'];
          } else if (mainTerm === 'priority') {
            contextCards = ['لا أفهم قواعد الأولوية', 'أي مركبة لها الأولوية؟'];
          } else if (mainTerm === 'sign') {
            contextCards = ['أي تفصيل في اللوحة فاتني؟', 'ماذا يجب أن أبحث عنه على هذه اللوحة؟'];
          } else {
            contextCards = ['أي جزء من السؤال فاتني؟', 'أي تفصيل مهم؟'];
          }
        } else {
          if (mainTerm === 'traffic light' || mainTerm === 'amber') {
            contextCards = ['I don\'t understand amber lights', 'When must I stop at amber?'];
          } else if (mainTerm === 'priority') {
            contextCards = ['I don\'t understand priority rules', 'Which vehicle has priority?'];
          } else if (mainTerm === 'sign') {
            contextCards = ['Which sign detail did I miss?', 'What should I look for on this sign?'];
          } else {
            contextCards = ['What part of the question did I miss?', 'Which detail is important?'];
          }
        }
      }

      const filteredCards = contextCards.filter((card: string) => {
        const cardLower = card.toLowerCase().trim();
        return !usedCards.some(used =>
          used.includes(cardLower) || cardLower.includes(used) || used === cardLower
        );
      });

      if (filteredCards.length >= 2) {
        return filteredCards.slice(0, 2);
      }
    }

    const templateCards = getTemplateSuggestions(language, questionText, subject, hasImage, genericCard, isCorrect, currentRound);
    const filteredTemplateCards = templateCards.filter(card => {
      const cardLower = card.toLowerCase().trim();
      return !usedCards.some(used =>
        used.includes(cardLower) || cardLower.includes(used) || used === cardLower
      );
    });

    if (filteredTemplateCards.length >= 2) {
      return filteredTemplateCards.slice(0, 2);
    } else if (currentRound === 2) {
      if (language === 'nl') {
        return ['Wat begreep ik verkeerd over deze regel?', 'Welk deel van dit scenario verwarde me?'];
      } else if (language === 'ar') {
        return ['ماذا فهمت بشكل خاطئ عن هذه القاعدة؟', 'أي جزء من هذا السيناريو أربكني؟'];
      }
      return ['What did I misunderstand about this rule?', 'Which part of this scenario confused me?'];
    } else {
      if (language === 'nl') {
        return ['Wat bedoel je precies?', 'Kun je dat uitleggen?'];
      } else if (language === 'ar') {
        return ['ماذا تقصد بالضبط؟', 'هل يمكنك شرح ذلك؟'];
      }
      return ['What do you mean exactly?', 'Can you explain that?'];
    }
  }
};
